import React from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { Reveal, Eyebrow, Crumbs, FaqList } from "../components/Shared";
import { IMG, SITE, CHALKIT_PRODUCTS } from "../data/mock";
import { trackGoalReviewBooking } from "../lib/analytics";

const GOAL_REVIEW_URL = CHALKIT_PRODUCTS.goalReview;

const gsFaqs = [
  { q: "What happens at a goal review?", a: "A free 20-minute conversation with a coach about your goals, training history and any limitations — no workout, no sales script. You leave with a recommended starting point and an optional free InBody body composition scan." },
  { q: "Does the goal review cost anything?", a: "No — the goal review is completely free, no credit card required and zero obligation to join." },
  { q: "Do I need to be in shape first?", a: "No. The goal review is a conversation, not a fitness test, and every program at the gym scales to your current ability." },
  { q: "Can I pick my appointment time online?", a: "Yes — booking is handled through Chalk It Pro, our scheduling system. Pick an available time, enter your contact information, complete the required waiver and you'll receive confirmation on the Boston Iron Grit schedule instantly." },
];

const RESERVE_ACTION = {
  "@context": "https://schema.org",
  "@type": "ReserveAction",
  target: {
    "@type": "EntryPoint",
    urlTemplate: "https://bostonirongrittraining.com/get-started",
    actionPlatform: ["https://schema.org/DesktopWebPlatform", "https://schema.org/MobileWebPlatform"],
  },
  result: { "@type": "Reservation", name: "Free 20-Minute Goal Review" },
  provider: { "@id": "https://bostonirongrittraining.com/#business" },
};

const BOOKING_STEPS = [
  "Pick an available time.",
  "Enter your contact information and complete the required waiver.",
  "Receive confirmation and appear on the Boston Iron Grit Goal Review schedule.",
];

export default function GetStarted() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Free Goal Review", path: "/get-started" },
  ];

  return (
    <>
      <Seo
        title="Book a Free Goal Review | Start at Boston Iron Grit, West Roxbury"
        description="Book a free goal review at Boston Iron Grit in West Roxbury — a 20-minute talk about your goals and the right first step. No workout, no pressure."
        path="/get-started"
        schemas={[breadcrumbSchema(crumbs), faqSchema(gsFaqs), RESERVE_ACTION]}
      />

      <section className="relative min-h-[90vh] bg-[#0A0A0B] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.rigClimb})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/90 to-[#0A0A0B]/60" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-14 items-start">
          {/* Left copy */}
          <Reveal>
            <Crumbs items={crumbs} />
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-[#9D0423]">// Step one of everything</p>
            <h1 className="mt-4 font-heading font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight">
              Book your free<br /><span className="text-[#9D0423]">goal review.</span>
            </h1>
            <p className="mt-6 text-neutral-300 text-base lg:text-lg leading-relaxed max-w-lg">
              A 20-minute conversation with a coach about your goals, training history and any limitations. No workout,
              no sales script — just an honest plan for your first step.
            </p>
            <ul className="mt-8 space-y-3.5">
              {[
                "We learn your goals and history — not test your fitness",
                "You get a recommended starting point (fundamentals, classes or PT)",
                "Optional InBody scan included, free",
                "Zero obligation — seriously",
              ].map((li) => (
                <li key={li} className="flex items-start gap-3 text-[15px] text-neutral-300">
                  <CheckCircle2 size={17} className="text-[#9D0423] mt-0.5 shrink-0" /> {li}
                </li>
              ))}
            </ul>
            <div className="mt-10" data-testid="getstarted-faq">
              <FaqList faqs={gsFaqs} title="Goal Review — FAQs" />
            </div>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">
              Prefer to talk? Call or text <a href={SITE.phoneHref} className="text-white hover:text-[#E56A79] transition-colors">{SITE.phone}</a>
            </p>
          </Reveal>

          {/* Booking card — direct Chalk It Pro reservation */}
          <Reveal delay={120}>
            <div className="bg-[#121214]/95 backdrop-blur border border-white/10 p-8 lg:p-10" data-testid="goal-review-booking-card">
              <Eyebrow>Book Online — Powered by Chalk It Pro</Eyebrow>
              <h2 className="font-heading font-extrabold uppercase text-3xl text-white tracking-tight leading-tight">
                Book Your Free Goal Review
              </h2>
              <p className="mt-4 text-neutral-300 text-[15px] leading-relaxed">
                Choose a convenient time for a free 20-minute conversation with a Boston Iron Grit coach. We will discuss
                your goals, training history and the best place to start. No pressure, no obligation and no credit card required.
              </p>

              <ol className="mt-8 space-y-4" data-testid="booking-steps">
                {BOOKING_STEPS.map((step, i) => (
                  <li key={step} className="flex items-start gap-4">
                    <span className="flex items-center justify-center w-8 h-8 shrink-0 font-mono text-[12px] bg-[#9D0423] text-white" aria-hidden="true">{i + 1}</span>
                    <span className="text-[14px] text-neutral-300 leading-relaxed pt-1.5">{step}</span>
                  </li>
                ))}
              </ol>

              <a
                href={GOAL_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-goal-review-button"
                onClick={() => trackGoalReviewBooking("get-started-booking-card", GOAL_REVIEW_URL)}
                className="mt-9 w-full inline-flex items-center justify-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-6 py-4 transition-colors"
              >
                Book Your Free Goal Review <ArrowRight size={15} />
              </a>
              <p className="mt-3 text-[12px] text-neutral-400" data-testid="no-credit-card-note">
                The goal review is free — no credit card required.
              </p>

              <p className="mt-6 pt-6 border-t border-white/10 text-[13px] text-neutral-400" data-testid="booking-help-line">
                Having trouble booking? Call or text{" "}
                <a href={SITE.phoneHref} className="text-white hover:text-[#E56A79] transition-colors">617-637-8900</a>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
